import { createPostExtensions } from "@growth/editor";
import { SiteTheme, TiptapDoc } from "@growth/schema";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import { useMemo } from "react";

import { Toolbar } from "./toolbar";
import { EditorWidgetNode } from "./widget-node-view";

type Props = {
  /** The blog's look, so the editor previews posts in its styles. */
  theme: SiteTheme;
  title: string;
  aiEnabled: boolean;
  initialContent: TiptapDoc;
  onChange: (doc: TiptapDoc) => void;
  onError: (message: string) => void;
};

/** Uncontrolled: seeded once from `initialContent`, then reports every change. */
export const RichTextEditor = ({
  theme,
  title,
  aiEnabled,
  initialContent,
  onChange,
  onError,
}: Props) => {
  const extensions = useMemo(
    () => [
      ...createPostExtensions({ widget: EditorWidgetNode }),
      Placeholder.configure({ placeholder: "Start writing…" }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editorProps: {
      attributes: {
        class: "cms-prose min-h-[28rem] px-8 py-6 focus:outline-none",
        "aria-label": "Post body",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getJSON() as TiptapDoc),
  });

  if (!editor) return null;

  return (
    <div className={`rounded-xl border border-line bg-white blog-${theme}`}>
      <Toolbar editor={editor} onError={onError} title={title} aiEnabled={aiEnabled} />
      <EditorContent editor={editor} />
    </div>
  );
};
